<template>
  <DialogComponent
    ref="dialogRef"
    dialogId="birthday-dialog"
    :type="dialogType"
    :noInnerPadding="true"
    @closed="onDialogClosed"
  >
    <template #default>
      <div class="flex flex-col h-full">
        <!-- Header with illustration -->
        <div class="text-center px-4 pt-6 pb-4 sm:px-6 sm:pt-8 sm:pb-4">
          <div
            class="w-20 h-20 mx-auto mb-4 bg-primary text-white rounded-full flex items-center justify-center shadow-lg shadow-primary/30"
          >
            <svg
              class="w-10 h-10"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C13.6569 2 15 3.34315 15 5V6H9V5C9 3.34315 10.3431 2 12 2Z"
                fill="currentColor"
              />
              <path
                d="M20 8H4V19C4 20.6569 5.34315 22 7 22H17C18.6569 22 20 20.6569 20 19V8Z"
                fill="currentColor"
                opacity="0.3"
              />
              <path
                d="M8 12C8.55228 12 9 11.5523 9 11C9 10.4477 8.55228 10 8 10C7.44772 10 7 10.4477 7 11C7 11.5523 7.44772 12 8 12Z"
                fill="currentColor"
              />
              <path
                d="M12 12C12.5523 12 13 11.5523 13 11C13 10.4477 12.5523 10 12 10C11.4477 10 11 10.4477 11 11C11 11.5523 11.4477 12 12 12Z"
                fill="currentColor"
              />
              <path
                d="M16 12C16.5523 12 17 11.5523 17 11C17 10.4477 16.5523 10 16 10C15.4477 10 15 10.4477 15 11C15 11.5523 15.4477 12 16 12Z"
                fill="currentColor"
              />
              <path
                d="M7 15C7.55228 15 8 15.4477 8 16C8 16.5523 7.55228 17 7 17C6.44772 17 6 16.5523 6 16C6 15.4477 6.44772 15 7 15Z"
                fill="currentColor"
              />
              <path
                d="M11 15C11.5523 15 12 15.4477 12 16C12 16.5523 11.5523 17 11 17C10.4477 17 10 16.5523 10 16C10 15.4477 10.4477 15 11 15Z"
                fill="currentColor"
              />
              <path
                d="M15 15C15.5523 15 16 15.4477 16 16C16 16.5523 15.5523 17 15 17C14.4477 17 14 16.5523 14 16C14 15.4477 14.4477 15 15 15Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h2 class="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Joyeux anniversaire! 🎉
          </h2>
          <p class="text-sm text-gray-500 leading-relaxed">
            Entrez votre date d'anniversaire pour recevoir des cadeaux de la
            part de Rapidoo
          </p>
        </div>

        <!-- Form content -->
        <div class="flex-1 px-4 pb-4 sm:px-6 sm:pb-6 flex flex-col">
          <form
            class="flex flex-col gap-4 h-full"
            v-bus:EVENTS.USER.SAVE_BIRTHDAY.submit.prevent
          >
            <div
              class="flex-1  bg-gray-50 rounded-xl p-4 px-24 flex items-center justify-center"
            >
              <DatePickerInline
                v-model="birthdayDate"
                :maxDate="new Date()"
                :minDate="new Date(1900, 0, 1)"
                ref="datePickerRef"
              />
              <input type="hidden" name="birthday" :value="birthdayDate" />
            </div>


            <!-- Actions -->
            <div
              class="flex flex-col sm:flex-row gap-3 pt-4 "
            >
              <button
                type="submit"
                class="flex-1 min-h-[48px] px-6 py-3.5 rounded-xl text-base font-semibold text-white bg-primary hover:-translate-y-0.5 shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200 flex items-center justify-center"
                :disabled="!birthdayDate || isLoading"
              >
                <span v-if="!isLoading">Enregistrer</span>
                <span v-else class="flex items-center justify-center">
                  <svg class="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                      fill="none"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
              </button>
              <button
                type="button"
                class="flex-1  rounded-xl text-base font-semibold text-gray-500  duration-200 flex items-center justify-center"
                v-bus:EVENTS.USER.SKIP_BIRTHDAY.click
              >
                Plus tard
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import DialogComponent from '@ui/dialog/DialogComponent.global.vue';
import DatePickerInline from '@ui/core/input/DatePickerInline.global.vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const { proxy } = getCurrentInstance();
const dialogRef = ref(null);
const datePickerRef = ref(null);
const birthdayDate = ref(null);
const isLoading = ref(false);

// Responsive dialog type
const dialogType = computed(() => {
  return window.innerWidth < 640 ? 'bottomsheet' : 'modal';
});

// Show dialog method
const show = () => {
  dialogRef.value?.show();
  // Emit event that dialog was shown
  bus.emit(constants.EVENTS.USER.BIRTHDAY_DIALOG_SHOWN);
};

// Hide dialog method
const hide = () => {
  dialogRef.value?.hide();
};

// Handle dialog closed
const onDialogClosed = () => {
  // Reset form
  birthdayDate.value = null;
  isLoading.value = false;
};

// Handle loading state
bus.on(constants.EVENTS.LOADING.START, () => {
  isLoading.value = true;
});

bus.on(constants.EVENTS.LOADING.FINISH, () => {
  isLoading.value = false;
});

// Handle success - close dialog and show success message
bus.on(constants.EVENTS.USER.SAVE_BIRTHDAY_SUCCESS, () => {
  hide();
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'Date enregistrée!',
    body: 'Vous recevrez des cadeaux le jour de votre anniversaire 🎁'
  });
});

// Handle success - close dialog and show success message
bus.on(constants.EVENTS.USER.UPDATE_SUCCESS, () => {
  hide();
});

// Handle error - show error message
bus.on(constants.EVENTS.USER.SAVE_BIRTHDAY_ERROR, (error) => {
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'Erreur',
    body: error?.message || 'Une erreur est survenue. Veuillez réessayer.',
    type: 'error'
  });
});

// Handle skip - close dialog
bus.on(constants.EVENTS.USER.SKIP_BIRTHDAY, () => {
  hide();
});

// Listen for show event
bus.on('birthday:show', show);

// Expose methods
defineExpose({
  show,
  hide
});

onMounted(() => {
  // Auto-show dialog after registration if user doesn't have birthday
  const userStore = proxy.$stores?.user;
  if (
    userStore?.state?.current &&
    !userStore.state.current.metadata?.birthday
  ) {
    // Delay slightly for better UX
    setTimeout(() => {
      show();
    }, 1000);
  }
});
</script>
